import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { AppShell } from '@/components/AppShell/AppShell';
import { useSessionStore } from '@/stores/session';
import { mintSessionFolder } from '@/lib/sessionFolder';
import { withSearch } from '@/lib/nav';
import styles from './Welcome.module.css';

const NAME_PATTERN = /^[\p{L}\p{M}\s.'-]{1,40}$/u;

export function Welcome() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const config = useSessionStore((s) => s.config);
  const setName = useSessionStore((s) => s.setName);
  const setSessionFolder = useSessionStore((s) => s.setSessionFolder);
  const existingFolder = useSessionStore((s) => s.sessionFolder);
  const existingName = useSessionStore((s) => s.name);
  const [name, setNameInput] = useState(existingName);
  const [hint, setHint] = useState<string | null>(null);

  // If a sessionFolder was already minted in this session (talent submitted
  // their name and is just hitting browser-Back), bounce them straight back
  // to /generate. Letting them edit the name here would diverge the upload
  // folder name from the file prefix and break downstream resolution. The
  // navigate(replace) on Continue isn't enough on its own — Safari's history
  // stack often retains the initial /:id load entry, so Back from /generate
  // can still land here.
  useEffect(() => {
    if (existingFolder && id) {
      navigate(withSearch(`/${id}/generate`, location.search), { replace: true });
    }
  }, [existingFolder, id, location.search, navigate]);

  if (!config) return null;
  if (existingFolder) return null;

  const trimmedName = name.trim();
  const isNameValid = NAME_PATTERN.test(trimmedName);

  const start = (e: FormEvent) => {
    e.preventDefault();
    if (!isNameValid) {
      setHint('Please enter a name between 1–40 letters.');
      return;
    }
    setName(trimmedName);
    // Mint sessionFolder + shortId on Start (vanilla `confirmName` parity,
    // legacy/index.html:3952-3964). Without this the Upload Version gate
    // (`!sessionFolder`) stays locked for the whole session because nothing
    // else writes the folder until the upload pipeline tries to use it.
    // Skip when a hydrated session already provided one. ToolLayout has a
    // matching lazy mint for pre-fix sessions that bypass Welcome entirely
    // (see `src/lib/sessionFolder.ts` for the rationale).
    if (!existingFolder) {
      const { folder, shortId } = mintSessionFolder(trimmedName);
      setSessionFolder(folder, shortId);
    }
    // Replace the history entry instead of pushing it so the browser Back
    // button skips Welcome — going back here would let the talent change
    // their name AFTER sessionFolder was already minted, which makes the
    // upload folder name diverge from the file prefix and breaks downstream
    // resolution. Once they're in /generate, name + folder are locked.
    navigate(withSearch(`/${id}/generate`, location.search), { replace: true });
  };

  const brandName =
    config.customBranding && config.brandName ? config.brandName : null;
  const greeting = brandName ? `Welcome, ${brandName}!` : 'Welcome!';
  const leadBranded = brandName
    ? `This TRO Voice Studio session is specifically set up for ${brandName} to generate Voice-Over files for your project. Your selected favorite files will be uploaded to TRO automatically when you finish.`
    : 'This access to TRO Voice Studio is specifically designed to generate Voice-Over files for your project. Your selected favorite files will be uploaded to our server automatically when you finish.';

  return (
    // animateLogo plays troLogoGrow on mount — mirrors legacy/index.html
    // lines 3033 / 3083 (initial screen-0 entry) and lines 3361 / 3370
    // (re-entry from later screens). Welcome is the only screen that
    // triggers the grow; navigating back here remounts AppShell so the
    // animation replays (matches vanilla's troLogoReset → troLogoGrow loop).
    <AppShell animateLogo>
      <main className={styles.wrap}>
        <div className={styles.content}>
          <h1 className={styles.title}>
            <i
              className={`ph-bold ph-waveform ${styles.titleIcon}`}
              aria-hidden="true"
            />{' '}
            Voice Studio
          </h1>
          <div className={styles.lead}>
            <p>{greeting}</p>
            <p>{leadBranded}</p>
            <p>
              On the top right corner you will find buttons to contact TRO as
              well as tips for the voice generation process.
            </p>
            <p>Please enter your name below to get started.</p>
          </div>

          {/* Soft browser-recommendation hint removed — non-Chromium browsers
              are now hard-blocked by BrowserGate (ToolLayout) before Welcome
              renders, so anyone reaching this screen is already on Chromium. */}

          <form onSubmit={start} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="welcomeName" className={styles.label}>
                Your name
              </label>
              <input
                id="welcomeName"
                className={styles.input}
                placeholder="e.g. Maria Müller"
                value={name}
                onChange={(e) => { setNameInput(e.target.value); setHint(null); }}
                autoFocus
                maxLength={60}
                autoComplete="name"
              />
              {hint && <div className={styles.hint}>{hint}</div>}
            </div>
            <div className={styles.actions}>
              <Button type="submit" disabled={!isNameValid}>
                Start Project <i className="ph-bold ph-arrow-right" />
              </Button>
            </div>
          </form>
        </div>
      </main>
    </AppShell>
  );
}
